import React, {useEffect, useMemo, useState} from "react";
import JobRest from "../../rest/JobRest.ts";
import ImageRest from "../../rest/ImageRest.ts";
import {useNavigate} from "react-router-dom";
import {IFeature} from "../../types/models/Feature.ts";
import {useCutJobApi} from "./useCutJobApi";
import {uniqBy} from "lodash";
import {IGauge, IGaugeSpec} from "../../types/models/Gauge.ts";
import {enqueueSnackbar} from "notistack";
import {GaugesSlider} from "./GaugesSlider";
import {FeatureSlider} from "./FeatureSlider";
import {JOB_STATUS, JOB_STATUS_CATEGORY} from "../../types/models/Job.ts";
import {Box, Container, Skeleton, Stack, Typography} from "@mui/material";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner.tsx";
import {CardSlider} from "./CardSlider";
import {EditableText, Expandable, ImageHeader} from "@laser-project/kyui";
import Statement from "../../components/Statements/Statement.tsx";
import {
    AirlineStops,
    Clear,
    ContentCopy,
    ContentPaste,
    ContentPasteSearch,
    Dangerous,
    DeleteForever,
    DoneOutlined,
    Download,
    Error,
    HourglassTop,
    InfoOutlined,
    Launch,
    MultipleStop,
    QueryStats,
    Report,
    RestartAlt,
    SignalWifiConnectedNoInternet4
} from "@mui/icons-material";
import {LoadingButton} from "@mui/lab";
import {renderJobStatusChips} from "../../components/StatusChips/JobStatusChips.tsx";
import {CUTTER_STATUS} from "../../types/models/Cutter.ts";
import {ISettingsStorage, loadSettings} from "../../types/ISettingsStorage.ts";
import CutterDialogBuilder from "./CutterDialog/CutterDialogBuilder.tsx";
import FileUtils from "../../utils/FileUtils.ts";
import useDeleteJobModal from "../../components/CustomModal/useDeleteJobModal.tsx";
import useResetJobModal from "../../components/CustomModal/useResetJobModal.tsx";
import useAbortJobModal from "../../components/CustomModal/useAbortJobModal.tsx";
import {MakeButton} from "./MakeButton";
import {CalibrationsSlider} from "../../components/Calibrations/CalibrationsSlider.tsx";
import {useLocalStorage} from "../../hooks/useLocalStorage/useLocalStorage.tsx";

export function CutJob({jobId}: Readonly<{ jobId?: string }>) {
    const navigate = useNavigate();

    const [settings] = useState<ISettingsStorage>(loadSettings());
    // Job Interfaces
    const jobRest = useMemo(() => new JobRest(), []);
    const imageRest = useMemo(() => new ImageRest(), []);

    const [filteredFeatures, setFilteredFeatures] = React.useState<IFeature[] | null>(null);
    const [images, setImages] = useState([])
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const {
        handleUserInputSubmit,
        handleStart,
        isSubmitting,
        activeActionId,
        job,
        assignCutter,
        cutters,
        lastErrorMessage,
        calibrations,
        fetchCalibrations
    } = useCutJobApi({jobId})

    const {openDialog, dialogComponent} = CutterDialogBuilder({job, assignCutter, cutters, imageRest})

    // Fetching features for job
    useEffect(() => {
        if (!jobId) {
            console.warn("Did not have jobId. Aborting")
            return;
        }
        jobRest
            .getFeaturesByJobId(jobId)
            .then(response => {
                const allFeatures = response.data.data;
                let features: IFeature[] = uniqBy(allFeatures, (feature: IFeature) => feature.groupingKey);
                const blacklistedDependencyKeys: string[] = []
                features = features.map(feature => {
                    const newFeature = {...feature};
                    const cleanedDeps: (IGauge | IGaugeSpec)[] = [];
                    newFeature.dependencies?.forEach(dep => {
                        if (!blacklistedDependencyKeys.includes(dep.groupingKey)) {
                            blacklistedDependencyKeys.push(dep.groupingKey);
                            cleanedDeps.push(dep);
                        }
                    })
                    newFeature.dependencies = cleanedDeps;
                    return newFeature;
                })

                features.forEach(
                    feature => feature.entityType = "feature"
                )

                setFilteredFeatures(features)
            })
    }, [jobId, jobRest]);

    const reload_images = () => {
       const existsCutplan = imageRest.checkImageExists(
            {"imageName": job?._id+"-cutplan.jpg" ?? ""}
        );
        const existsCurrent = imageRest.checkImageExists(
            {"imageName": job?._id+"-current.jpg" ?? ""}
        );
        Promise.all([
            existsCutplan,
            existsCurrent
        ]).then(([cutplanExists, currentExists]) => {
            const newImages = [imageRest.getUrlById(job?.imageId ?? "")];
            cutplanExists && newImages.push(
                imageRest.getUrlByName(job?._id+"-cutplan.jpg" ?? "", true)
        );
            currentExists && newImages.push(
                imageRest.getUrlByName(job?._id+"-current.jpg" ?? "", true)
        );
            setImages(newImages);
        });
    }

    useEffect(() => {
        // if ([JOB_STATUS.EXECUTION_SUCCESS, JOB_STATUS.EXECUTION_FAILED].includes(job?.status)) {
        //     currentImageIndex != 2 && setCurrentImageIndex(2);
        //     new Promise(r => setTimeout(r, 2000)).then(() => reload_images())
        // }
        // else if ([JOB_STATUS.EXECUTION_FABRICATING].includes(job?.status)) {
        //     currentImageIndex != 1 && setCurrentImageIndex(1);
        // }
        // else {
        //     currentImageIndex != 0 && setCurrentImageIndex(0);
        // }
        reload_images();
    }, [job?.status]);

    useEffect(() => {
        if (!lastErrorMessage || lastErrorMessage === "") {
            return;
        }
        enqueueSnackbar(lastErrorMessage, {
            variant: 'error',
        })
    }, [lastErrorMessage]);

    const openDeleteModal = useDeleteJobModal({
        job,
        jobRest,
        imageRest,
        afterDelete: () => navigate("/")
    });

    const openResetModal = useResetJobModal({
        job,
        jobRest,
        imageRest,
        afterReset: (newJob) => navigate("/job/" + newJob.data.data._id)
    });

    const openAbortModel = useAbortJobModal({
        job,
        jobRest,
        imageRest
    })

    const [calibrationClipboard, setCalibrationClipboard] = useLocalStorage("calibrationClipboard", null);

    function renderGauges() {
        return <GaugesSlider filteredFeatures={filteredFeatures}/>
    }

    function renderFeatures() {
        return <FeatureSlider filteredFeatures={filteredFeatures}/>
    }

    function renderCalibrations() {
        if (job) {
            return <CalibrationsSlider
                jobId={job._id}
                calibrations={calibrations}
                fetchCalibrations={fetchCalibrations}
            />
        }
        return <Skeleton height={500}/>
    }

    function renderCalibrationSection(){
        if (!job) {
            return <></>
        }
        if ([JOB_STATUS.DELETED].includes(job.status)) {
            return <></>
        }
        return settings.showCalibrationsPanelJobPage ? <Expandable title="Calibrations">
            {renderCalibrations()}
        </Expandable> : <></>
    }

    function renderJobPerStage() {
        if (!job) {
            return <Skeleton height={500}/>
        }

        const restartButtonGroup = <Box sx={{display: 'flex', gap: 2, mt: 2}}>
            <LoadingButton sx={{mt: 2}}
                           color={"secondary"}
                           onClick={() => {
                               assignCutter(job.cutterId);
                           }}
                           disabled={!job?.cutter || (JOB_STATUS_CATEGORY.RUNNING).includes(job?.status)}
                           startIcon={<QueryStats/>}
                           variant={"outlined"}>Re-analyze job</LoadingButton>
            <LoadingButton sx={{mt: 2}}
                           color={"secondary"}
                           onClick={() => openResetModal()}
                           startIcon={<RestartAlt/>}
                           variant={"outlined"}>Reset job</LoadingButton>
        </Box>;

        const selectCutterButton = <LoadingButton sx={{mt: 2}}
                                                  color={"secondary"}
                                                  onClick={openDialog}
                                                  startIcon={<MultipleStop/>}
                                                  variant={"outlined"}>{job.cutterId ? "Change cutter" : "Select cutter"}</LoadingButton>;
        switch (job?.status) {
            case JOB_STATUS.UNASSIGNED: {
                return (
                    <Statement icon={<InfoOutlined/>} message={"To analyze this model, please assign a cutter."}
                               action={selectCutterButton}/>
                )
            }
            case JOB_STATUS.ANALYSIS_PENDING: {
                if (job.cutter?.status == CUTTER_STATUS.OFFLINE) {
                    return (<Statement
                        icon={<SignalWifiConnectedNoInternet4/>}
                        message={"Cutter is offline"}
                        reason={"Select different cutter or wait until cutter is online, to analyze this model."}
                        action={selectCutterButton}
                    />);
                }
                let reason = null;
                if (job.cutter?.state.isCutterBusy && job.cutter?.state.jobId !== job._id) {
                    reason = "The selected cutter is currently busy.";
                }

                return (
                    <Stack spacing={3}>
                        <LoadingSpinner message={"Waiting for cutter to analyze this model"} reason={reason}/>
                        {
                            settings.showFeaturesPanelJobPage ? <Expandable title={"Features"}>
                                {renderFeatures()}
                            </Expandable> : <></>
                        }

                    </Stack>
                )
            }
            case JOB_STATUS.ANALYSIS_STARTED: {
                if (job.cutter?.status == CUTTER_STATUS.OFFLINE) {
                    return (<Statement
                        icon={<SignalWifiConnectedNoInternet4/>}
                        message={"Cutter is offline"}
                        reason={"Lost connection to cutter while analyzing this model."}
                    />);
                }
                return (
                    <Stack spacing={3}>
                        <LoadingSpinner message={"Model is being analyzed"}/>
                        {
                            settings.showFeaturesPanelJobPage ? <Expandable title={"Features"}>
                                {renderFeatures()}
                            </Expandable> : <></>
                        }

                    </Stack>
                )
            }
            case JOB_STATUS.EXECUTION_ABORTING: {
                return (
                    <Stack spacing={3}>
                        <LoadingSpinner message={"Aborting job"}/>
                    </Stack>
                )
            }
            case JOB_STATUS.ANALYSIS_FAILED:
                return (
                    <Box pb={10}>
                        <Statement
                            icon={<Report/>}
                            message={"Your model could not be analyzed!"}
                            reason={job.statusReason}
                            action={restartButtonGroup}
                        />
                    </Box>
                )
            case JOB_STATUS.ANALYSIS_COMPLETE: {
                let statement = null;
                if (job.cutter?.status == CUTTER_STATUS.OFFLINE) {
                    statement = <Statement
                        icon={<SignalWifiConnectedNoInternet4/>}
                        message={"Cutter is offline"}
                        reason={"Select different cutter or wait until cutter is online, to make this job."}
                        action={selectCutterButton}
                    />;
                } else if (job.cutter?.state.isCutterBusy && job.cutter?.state.jobId !== job._id) {
                    statement = <Statement
                        icon={<HourglassTop/>}
                        message={"Cutter is busy"}
                        reason={"Select different cutter or wait until cutter is idle, to make this job."}
                        action={selectCutterButton}
                    />;
                }
                return (<>
                    {statement}
                    <Stack spacing={3}>
                        {
                            settings.showGaugePanelJobPage ?
                                <Expandable title={"Gauges"}> {renderGauges()} </Expandable> : <></>
                        }
                        {
                            settings.showFeaturesPanelJobPage ?
                                <Expandable title={"Features"}> {renderFeatures()} </Expandable> : <></>
                        }
                    </Stack>
                </>)
            }
            case JOB_STATUS.EXECUTION_PENDING: {
                if (job.cutter?.status == CUTTER_STATUS.OFFLINE) {
                    return (<Statement
                        icon={<SignalWifiConnectedNoInternet4/>}
                        message={"Cutter is offline"}
                        reason={"Lost connection to cutter while execute this job."}
                    />);
                }
                return (
                    <Stack spacing={3}>
                        <LoadingSpinner message={"Waiting for cutter to execute this job"}/>
                        {settings.showFeaturesPanelJobPage ? <>{renderFeatures()}</> : <></>}
                    </Stack>
                )
            }
            case JOB_STATUS.EXECUTION_STARTED: {
                if (job.cutter?.status == CUTTER_STATUS.OFFLINE) {
                    return (<Statement
                        icon={<SignalWifiConnectedNoInternet4/>}
                        message={"Cutter is offline"}
                        reason={"Lost connection to cutter while execute this job."}
                    />);
                }
                return (
                    <Stack spacing={3}>
                        <LoadingSpinner message={"Cutter is preparing to execute this job"}/>
                        {settings.showFeaturesPanelJobPage ? <>{renderFeatures()}</> : <></>}
                    </Stack>
                )
            }
            case JOB_STATUS.EXECUTION_WAITING_FOR_INPUT:
            case JOB_STATUS.EXECUTION_CALIBRATING:
            case JOB_STATUS.EXECUTION_FABRICATING:
                if (job.cutter?.status == CUTTER_STATUS.OFFLINE) {
                    return (<Statement
                        icon={<SignalWifiConnectedNoInternet4/>}
                        message={"Cutter is offline"}
                        reason={"Lost connection to cutter while execute this job."}
                    />);
                }
                return (
                    <Stack spacing={3}>
                        <CardSlider
                            handleUserInputSubmit={handleUserInputSubmit}
                            elements={job.actionOrder ?? []}
                            activeElementId={activeActionId}
                            currentSubmitting={isSubmitting}
                        />
                        {
                            settings.showGaugePanelJobPage ? <Expandable title={"Gauges"}>
                                {renderGauges()}
                            </Expandable> : <></>
                        }

                        {
                            settings.showFeaturesPanelJobPage ? <Expandable title={"Features"}>
                                {renderFeatures()}
                            </Expandable> : <></>
                        }
                        <canvas height={1000}>

                        </canvas>
                    </Stack>
                )
            case JOB_STATUS.EXECUTION_SUCCESS:
                return (
                    <Box pb={10}>
                        <Statement icon={<DoneOutlined/>} message={"Your model is done!"} huge={true}/>
                    </Box>
                )
            case JOB_STATUS.EXECUTION_FAILED:
                return (
                    <Box pb={10}>
                        <Statement
                            icon={<Report/>}
                            message={"Your model could not be produced!"}
                            reason={job.statusReason}
                            action={restartButtonGroup}
                        />
                    </Box>
                )
            case JOB_STATUS.DELETED:
                return (
                    <Box pb={10}>
                        <Statement
                            icon={<DeleteForever/>}
                            message={"This model has been deleted."}
                            reason={job.statusReason}
                        />
                    </Box>
                )
            default:
                return (
                    <Box pb={10}>
                        <Statement icon={<Error/>} message={"Unknown job status"}/>
                    </Box>
                )
        }
    }

    function renderImageHeaderActions() {
        const makeButton = <MakeButton job={job} handleStart={handleStart}/>;

        const selectCutterButton = <LoadingButton
            key={"selectCutterButton"}
            color={"secondary"}
            onClick={openDialog}
            disabled={!(JOB_STATUS_CATEGORY.CREATED).includes(job?.status)}
            variant={"contained"}
            startIcon={<MultipleStop/>}
        >Select Cutter</LoadingButton>;

        if (!job?.cutter) {
            return [selectCutterButton];
        } else {
            return [makeButton];
        }
    }

    if (!jobId) {
        return <Statement icon={<Clear/>} message={"No Job Specified"}/>
    }

    return [
        <Box sx={{width: '100%', overFlowY: ""}}>
            <Container sx={{p: 4}}>
                <Stack spacing={3}>
                    <ImageHeader
                        images={images}
                        currentImageIndex={currentImageIndex}
                        isLoading={!job}
                        menuActions={[
                            ...(settings.showChangeCutter ? [
                                {
                                    label: "Change cutter",
                                    onClick: openDialog,
                                    disabled: !(JOB_STATUS_CATEGORY.CREATED).includes(job?.status),
                                    icon: <MultipleStop/>,
                                },
                                {
                                    label: "Open cutter page",
                                    onClick: () => window.open(`/cutter/${job?.cutterId}`, '_blank'),
                                    disabled: !job?.cutter,
                                    icon: <Launch/>,
                                },
                            ] : []),

                            {
                                label: "Abort",
                                onClick: () => openAbortModel(),
                                icon: <Dangerous/>,
                                disabled: !((JOB_STATUS_CATEGORY.EXECUTING).includes(job?.status)),
                                dividerAbove: true,
                            },
                            ...(settings.showReanalyze ? [
                                {
                                    label: "Re-analyze",
                                    onClick: () => {
                                        if (!job.cutterId) return;
                                        assignCutter(job.cutterId);
                                    },
                                    disabled: !job?.cutter || (JOB_STATUS_CATEGORY.RUNNING).includes(job?.status),
                                    icon: <QueryStats/>
                                },
                            ] : []),
                            {
                                label: "Reset",
                                onClick: () => openResetModal(),
                                icon: <RestartAlt/>
                            },

                            {
                                label: "Download cutplan",
                                onClick: () => FileUtils.downloadStringAsFile(`${job.title}.cut`, job.cutplan),
                                icon: <Download/>,
                                dividerAbove: true,
                            },
                            ...(settings.showStepByStepOptionJobPage ? [
                                {
                                    label: "Step by step",
                                    onClick: () => navigate("/job/" + jobId + "/stepByStep"),
                                    icon: <AirlineStops/>,
                                    disabled: job?.status !== JOB_STATUS.ANALYSIS_COMPLETE || job.cutter?.state.isCutterBusy || job.cutter?.status === CUTTER_STATUS.OFFLINE
                                }
                            ] : []),

                            ...(settings.showCalibrationOptions ? [
                                {
                                    dividerAbove: true,
                                    disabled: !calibrationClipboard,
                                    label: "Paste Calibration",
                                    onClick: () => {
                                        jobRest.update(job._id, {
                                            calibrationIds: [...new Set([...(job.calibrationIds ?? []), calibrationClipboard])]
                                        }).then(() => {
                                            fetchCalibrations().then(() => {
                                                setCalibrationClipboard(null);
                                                enqueueSnackbar("Pasted Calibration", {
                                                    variant: "success",
                                                    autoHideDuration: 2000,
                                                });
                                            });
                                        });
                                    },
                                    icon: calibrationClipboard ? <ContentPaste/> : <ContentPasteSearch/>,
                                }
                            ] : []),

                            {
                                label: "Duplicate",
                                onClick: () => jobRest.duplicate(job._id).then((newJob) => navigate("/job/" + newJob.data.data._id)),
                                icon: <ContentCopy/>,
                                dividerAbove: true,
                            },
                            {
                                label: "Delete",
                                onClick: () => openDeleteModal(),
                                icon: <DeleteForever/>,
                            },
                        ]}
                        actions={renderImageHeaderActions()}
                    />
                    <Stack spacing={1}>
                        <Typography variant={"h1"}>
                            <EditableText
                                value={job?.title}
                                placeholderText="Unnamed job"
                                onSubmit={(title) => jobRest.update(jobId, {title: title})}
                                allowEdit
                                loading={!job}
                            />
                        </Typography>
                        {renderJobStatusChips(job)}
                    </Stack>
                    {renderJobPerStage()}
                    {renderCalibrationSection()}

                </Stack>
            </Container>
        </Box>,
        dialogComponent,
    ]

}